import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Sparkles, Globe, Hash, Activity, TrendingUp, TrendingDown, Minus,
  Newspaper, Palette
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MoodAnalysisModal = ({ mood, color, events, keywords, isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!mood) return null;

  const intensity = mood.intensity || 0.5;
  const sourcesCount = mood.sources_used?.length || 71;
  const headlinesCount = mood.headlines_analyzed || 319;
  const regionalKeywords = mood.regional_keywords || {};
  const topKeywords = keywords || mood.keywords || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="apple-card border-0 max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto modal-mobile-optimized modal-glow"
        style={{
          '--modal-glow-color': color || 'rgba(255,255,255,0.2)',
          '--modal-glow-color-faded': color ? `${color}50` : 'rgba(255,255,255,0.15)',
          '--modal-glow-color-dim': color ? `${color}30` : 'rgba(255,255,255,0.08)',
        }}
      >
        <DialogHeader>
          <DialogTitle className="apple-headline text-lg flex items-center gap-3">
            <motion.div 
              className="w-5 h-5 rounded-full"
              style={{ backgroundColor: color }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span style={{ color }}>{mood.adjective.toUpperCase()}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="mood-analysis-content mt-4 space-y-5">
          {/* Color & Intensity Section */}
          <div className="mood-analysis-section">
            <div className="mood-analysis-section-header">
              <Palette size={14} />
              <span>{t('colorAndIntensity') || 'Color & Intensity'}</span>
            </div>
            <div className="flex items-center gap-4 mt-3">
              <motion.div 
                className="w-16 h-16 rounded-xl border border-white/10"
                style={{ backgroundColor: color }}
                animate={{ 
                  boxShadow: [`0 0 20px ${color}40`, `0 0 30px ${color}60`, `0 0 20px ${color}40`]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <div className="flex-1">
                <p className="font-semibold" style={{ color: 'var(--apple-text-primary)' }}>
                  {mood.pantone?.name || 'Custom Color'}
                </p>
                <p className="text-xs font-mono" style={{ color: 'var(--apple-text-tertiary)' }}>
                  {color}
                </p>
                <div className="mt-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--apple-text-tertiary)' }}>Intensity</span>
                    <span style={{ color: 'var(--apple-text-secondary)' }}>{Math.round(intensity * 100)}%</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ backgroundColor: 'var(--apple-bg-tertiary)' }}>
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ backgroundColor: color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${intensity * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contributing Factors */}
          <div className="mood-analysis-section">
            <div className="mood-analysis-section-header">
              <Activity size={14} />
              <span>{t('contributingFactors') || 'Contributing Factors'}</span>
            </div>
            <div className="flex gap-3 mt-3 flex-wrap">
              <div className="mood-analysis-factor">
                <Newspaper size={14} />
                <span>{headlinesCount} headlines analyzed</span>
              </div>
              <div className="mood-analysis-factor">
                <Globe size={14} />
                <span>{sourcesCount} global sources</span>
              </div>
            </div>
          </div>

          {/* Key Topics */}
          {topKeywords.length > 0 && (
            <div className="mood-analysis-section">
              <div className="mood-analysis-section-header">
                <Hash size={14} />
                <span>{t('keyTopics') || 'Key Topics Driving This Mood'}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {topKeywords.slice(0, 8).map((kw, idx) => (
                  <motion.span 
                    key={idx} 
                    className="mood-analysis-keyword"
                    style={{ borderColor: `${color}40`, color }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    {typeof kw === 'string' ? kw : kw.text || kw.word || kw}
                  </motion.span>
                ))}
              </div>
            </div>
          )}

          {/* Regional Trends */}
          {Object.keys(regionalKeywords).length > 0 && (
            <div className="mood-analysis-section">
              <div className="mood-analysis-section-header">
                <Globe size={14} />
                <span>{t('regionalTrends') || 'Regional Trends'}</span>
              </div>
              <div className="mood-analysis-regions mt-3">
                {Object.entries(regionalKeywords).slice(0, 4).map(([region, trends], idx) => (
                  <motion.div 
                    key={region} 
                    className="mood-analysis-region"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <span className="mood-analysis-region-name">{region}</span>
                    <span className="mood-analysis-region-keywords">
                      {Array.isArray(trends) ? trends.slice(0, 2).join(', ') : trends}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Major Events */}
          {events && events.length > 0 && (
            <div className="mood-analysis-section">
              <div className="mood-analysis-section-header">
                <TrendingUp size={14} />
                <span>{t('majorEvents') || 'Major Events'}</span>
              </div>
              <div className="space-y-2 mt-3">
                {events.slice(0, 3).map((event, idx) => (
                  <motion.div 
                    key={idx} 
                    className={`mood-analysis-event ${event.sentiment || 'neutral'}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <span className="mood-analysis-event-icon">
                      {event.sentiment === 'positive' ? <TrendingUp size={12} /> : 
                       event.sentiment === 'negative' ? <TrendingDown size={12} /> : 
                       <Minus size={12} />}
                    </span>
                    <span className="mood-analysis-event-text">
                      {event.event || event.headline || event}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Synopsis */}
          <div className="mood-analysis-synopsis">
            <Sparkles size={14} style={{ color: 'var(--apple-text-tertiary)', flexShrink: 0 }} />
            <p>
              The global mood is currently <strong style={{ color }}>{mood.adjective}</strong> with an 
              intensity of {Math.round(intensity * 100)}%. This assessment is based on real-time analysis 
              of {headlinesCount} headlines from {sourcesCount} news sources worldwide.
              {topKeywords.length > 0 && ` Key themes include ${topKeywords.slice(0, 3).map(k => typeof k === 'string' ? k : k.text || k.word).join(', ')}.`}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { MoodAnalysisModal };
